import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { calculateHourlyRate, calculateAnnualFromHourly } from '../utils/taxCalculations';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';

const SalaryToHourly = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'UK Salary to Hourly Rate Converter | Calculate Hourly Wage from Annual Salary',
      description: 'Convert between UK annual salary and hourly rates. Calculate what your yearly salary means per hour, or determine annual earnings from an hourly wage. Free calculator for 2025/26.',
      keywords: 'salary to hourly, hourly rate calculator, UK hourly wage, annual salary converter, salary per hour, hourly to annual salary',
      ogTitle: 'UK Salary to Hourly Rate Converter',
      ogDescription: 'Convert between UK annual salary and hourly rates. Calculate what your yearly salary means per hour.',
      canonical: `${defaultMeta.siteUrl}/salary-to-hourly`,
    });
  }, []);

  const [annualSalary, setAnnualSalary] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState('37.5');
  const [activeTab, setActiveTab] = useState('salaryToHourly');

  const handleSalaryChange = (value) => {
    setAnnualSalary(value);
    if (value && parseFloat(value) > 0) {
      const hourly = calculateHourlyRate(parseFloat(value), parseFloat(hoursPerWeek));
      setHourlyRate(hourly.toFixed(2));
    } else {
      setHourlyRate('');
    }
  };

  const handleHourlyChange = (value) => {
    setHourlyRate(value);
    if (value && parseFloat(value) > 0) {
      const annual = calculateAnnualFromHourly(parseFloat(value), parseFloat(hoursPerWeek));
      setAnnualSalary(annual.toFixed(0));
    } else {
      setAnnualSalary('');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
          Salary to Hourly Rate Converter (UK)
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Convert between annual salary and hourly rates. Calculate what your yearly salary means per hour, 
          or determine annual earnings from an hourly wage.
        </p>
      </motion.section>

      {/* Calculator */}
      <motion.section 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="space-y-6">
          {/* Hours per week input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hours per Week
            </label>
            <input
              type="number"
              value={hoursPerWeek}
              onChange={(e) => {
                setHoursPerWeek(e.target.value);
                if (activeTab === 'salaryToHourly' && annualSalary) {
                  handleSalaryChange(annualSalary);
                } else if (hourlyRate) {
                  handleHourlyChange(hourlyRate);
                }
              }}
              placeholder="37.5"
              step="0.5"
              min="1"
              max="80"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">Standard full-time is 37.5 hours per week</p>
          </div>

          {/* Tab buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setActiveTab('salaryToHourly')}
              className={`px-4 py-3 rounded-lg border font-medium transition-colors ${
                activeTab === 'salaryToHourly'
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Salary → Hourly
            </button>
            <button
              onClick={() => setActiveTab('hourlyToSalary')}
              className={`px-4 py-3 rounded-lg border font-medium transition-colors ${
                activeTab === 'hourlyToSalary'
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Hourly → Salary
            </button>
          </div>

          {/* Input fields */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Salary (£)
              </label>
              <input
                type="number"
                value={annualSalary}
                onChange={(e) => handleSalaryChange(e.target.value)}
                placeholder="e.g. 40000"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                readOnly={activeTab === 'hourlyToSalary'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hourly Rate (£)
              </label>
              <input
                type="number"
                value={hourlyRate}
                onChange={(e) => handleHourlyChange(e.target.value)}
                placeholder="e.g. 20.51"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                readOnly={activeTab === 'salaryToHourly'}
              />
            </div>
          </div>

          {/* Results */}
          {(annualSalary && hourlyRate) && (
            <motion.div 
              className="bg-blue-50 border border-blue-200 rounded-lg p-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="font-semibold text-gray-900 mb-4">Conversion Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Yearly</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(parseFloat(annualSalary))}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Monthly</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(parseFloat(annualSalary) / 12)}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Weekly</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(parseFloat(annualSalary) / 52)}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Hourly</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(parseFloat(hourlyRate))}</p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* Comprehensive Guide */}
      <motion.section 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Complete Guide to UK Salary to Hourly Conversion</h2>
          <p className="text-gray-700 mb-6 leading-relaxed">
            Understanding how to convert between annual salary and hourly rates is essential for comparing job offers, negotiating contracts, and understanding your true earning potential. This guide explains everything you need to know about UK salary and hourly wage conversions.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
            <h3 className="font-semibold text-gray-900 mb-4 text-lg">How Salary to Hourly Conversion Works</h3>
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>Basic Formula:</strong> To convert annual salary to hourly rate, divide your annual salary by the total number of hours you work per year.
              </p>
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <p className="font-mono text-xs">
                  Hourly Rate = Annual Salary ÷ (Hours per Week × 52 weeks)
                </p>
              </div>
              <p>
                <strong>Example:</strong> If you earn £40,000 per year and work 37.5 hours per week:
              </p>
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <p className="font-mono text-xs">
                  £40,000 ÷ (37.5 × 52) = £40,000 ÷ 1,950 = £20.51 per hour
                </p>
              </div>
              <p>
                <strong>Reverse Calculation:</strong> To convert hourly rate to annual salary, multiply your hourly rate by hours per week, then by 52 weeks.
              </p>
            </div>
          </div>
          
          <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-100">
            <h3 className="font-semibold text-gray-900 mb-4 text-lg">UK Standard Working Hours</h3>
            <div className="space-y-3 text-gray-700 text-sm">
              <div>
                <h4 className="font-medium mb-2">Full-Time Employment:</h4>
                <ul className="space-y-1 ml-4">
                  <li>• Standard: 37.5 hours per week (most common)</li>
                  <li>• Alternative: 40 hours per week (some employers)</li>
                  <li>• Annual hours: 1,950 (37.5h) or 2,080 (40h)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Part-Time Employment:</h4>
                <ul className="space-y-1 ml-4">
                  <li>• Typically: 16-30 hours per week</li>
                  <li>• Varies significantly by employer</li>
                  <li>• Pro-rata benefits and holiday entitlement</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-3 border border-indigo-200 mt-3">
                <p className="text-xs leading-relaxed">
                  <strong>Working Time Regulations:</strong> The maximum working week in the UK is 48 hours, though employees can opt out. Most full-time roles are 35-40 hours per week.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">Important Considerations</h3>
          <div className="grid md:grid-cols-2 gap-6 text-gray-700 text-sm">
            <div>
              <h4 className="font-medium mb-2 text-amber-900">What's Included in Salary:</h4>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Base salary (gross pay before tax)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✓</span>
                  <span>Regular bonuses (if guaranteed)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✗</span>
                  <span>Overtime pay (calculated separately)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✗</span>
                  <span>Benefits in kind (company car, health insurance)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>✗</span>
                  <span>Pension contributions (employer or employee)</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2 text-amber-900">Factors Affecting Hourly Rate:</h4>
              <ul className="space-y-2">
                <li>• <strong>Overtime:</strong> Usually paid at 1.5x or 2x normal rate</li>
                <li>• <strong>Shift work:</strong> May include shift allowances</li>
                <li>• <strong>Holiday entitlement:</strong> Typically 25-28 days paid leave</li>
                <li>• <strong>Bank holidays:</strong> Usually 8 paid days per year</li>
                <li>• <strong>Unpaid breaks:</strong> Lunch breaks may not count as working hours</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">UK National Minimum Wage & Living Wage</h3>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            The UK has minimum wage rates that vary by age. As of April 2024, the National Living Wage (for workers aged 21 and over) is £11.44 per hour. This means any full-time worker should earn at least £22,308 per year (37.5 hours × 52 weeks × £11.44).
          </p>
          <div className="bg-white rounded-lg p-4 border border-emerald-200 text-sm">
            <h4 className="font-medium mb-2">Minimum Wage Rates (2024/25):</h4>
            <ul className="space-y-1 text-gray-700">
              <li>• Age 21+: £11.44 per hour (National Living Wage)</li>
              <li>• Age 18-20: £8.60 per hour</li>
              <li>• Age 16-17: £6.40 per hour</li>
              <li>• Apprentice rate: £6.40 per hour (first year, then age-appropriate rate)</li>
            </ul>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="font-semibold text-gray-900 mb-4 text-lg">Comparing Job Offers: Salary vs Hourly</h3>
          <p className="text-gray-700 mb-4 text-sm leading-relaxed">
            When comparing job offers, it's important to convert everything to the same basis. A £35,000 salary at 40 hours per week is actually a lower hourly rate (£16.83) than a £32,000 salary at 37.5 hours per week (£16.41). Always calculate the hourly equivalent to make fair comparisons.
          </p>
          <div className="bg-white rounded-lg p-4 border border-slate-300 text-sm">
            <h4 className="font-medium mb-2">Quick Comparison Tips:</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Convert both offers to hourly rates using the same hours per week</li>
              <li>• Consider total compensation (salary + benefits + pension)</li>
              <li>• Factor in overtime opportunities if applicable</li>
              <li>• Account for commute time and costs</li>
              <li>• Consider career progression and learning opportunities</li>
            </ul>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default SalaryToHourly;